import { Icon } from '../../components/icons/Icon'
import { Popover } from './Popover'
import type { ColumnDef } from './types'

export interface ColumnManagerMenuProps<T> {
  columns: ColumnDef<T>[]
  columnOrder: string[]
  hiddenColumnIds: Set<string>
  pinnedColumnIds: Set<string>
  onToggleHidden: (id: string) => void
  onMove: (id: string, direction: -1 | 1) => void
  onTogglePin: (id: string) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Persisted resize widths (IB-27 S3) — optional. When present alongside `onColumnWidthChange`,
   * a column whose persisted width diverges from its default gets a "Reset width" affordance here
   * (the natural place to undo a resize without knowing the `Shift+Left/Right` shortcut).
   */
  columnWidths?: Record<string, number> | undefined
  onColumnWidthChange?: ((id: string, width: number) => void) | undefined
}

/**
 * Show/hide, reorder (keyboard equivalent — move up/down — per
 * design-system.md §3.2 "Reorder has keyboard equivalent"), pin-left.
 * Case & Fiscal-year appear here too, grayed with a tooltip — the
 * future-proofed-shape pattern (IB-4 UX spec).
 */
export function ColumnManagerMenu<T>({
  columns,
  columnOrder,
  hiddenColumnIds,
  pinnedColumnIds,
  onToggleHidden,
  onMove,
  onTogglePin,
  open,
  onOpenChange,
  columnWidths,
  onColumnWidthChange,
}: ColumnManagerMenuProps<T>) {
  const byId = new Map(columns.map((c) => [c.id, c]))
  const ordered = columnOrder.map((id) => byId.get(id)).filter((c): c is ColumnDef<T> => Boolean(c))

  return (
    <Popover
      label="Manage columns"
      open={open}
      onOpenChange={onOpenChange}
      width={320}
      triggerContent={
        <>
          <Icon name="columns" size={14} /> Columns
        </>
      }
    >
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {ordered.map((column, index) => {
          const disabled = Boolean(column.disabledReason)
          const hidden = hiddenColumnIds.has(column.id)
          const pinned = pinnedColumnIds.has(column.id)
          const persistedWidth = columnWidths?.[column.id]
          const isResized = !disabled && typeof persistedWidth === 'number' && persistedWidth !== column.width
          return (
            <li
              key={column.id}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 2px' }}
              title={column.disabledReason}
            >
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <button
                  type="button"
                  className="iw-icon-btn-sm"
                  aria-label={`Move ${column.header} up`}
                  disabled={index === 0}
                  onClick={() => onMove(column.id, -1)}
                >
                  <Icon name="chevron-up" size={12} />
                </button>
                <button
                  type="button"
                  className="iw-icon-btn-sm"
                  aria-label={`Move ${column.header} down`}
                  disabled={index === ordered.length - 1}
                  onClick={() => onMove(column.id, 1)}
                >
                  <Icon name="chevron-down" size={12} />
                </button>
              </span>
              <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, opacity: disabled ? 0.5 : 1 }}>
                <input
                  type="checkbox"
                  checked={!hidden}
                  disabled={disabled}
                  onChange={() => onToggleHidden(column.id)}
                />
                <span style={{ fontSize: 'var(--iw-size-sm)' }}>{column.header}</span>
              </label>
              {column.pinnable ? (
                <button
                  type="button"
                  className="iw-icon-btn-sm"
                  aria-pressed={pinned}
                  aria-label={pinned ? `Unpin ${column.header}` : `Pin ${column.header} left`}
                  onClick={() => onTogglePin(column.id)}
                  style={pinned ? { color: 'var(--iw-accent)' } : undefined}
                >
                  <Icon name="pin" size={13} />
                </button>
              ) : null}
              {isResized && onColumnWidthChange ? (
                <button
                  type="button"
                  className="iw-icon-btn-sm"
                  aria-label={`Reset ${column.header} column width`}
                  onClick={() => onColumnWidthChange(column.id, column.width)}
                >
                  <Icon name="refresh-cw" size={12} />
                </button>
              ) : null}
            </li>
          )
        })}
      </ul>
    </Popover>
  )
}
